// @ts-nocheck
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ShieldCheck, ShieldAlert, Clock, Upload } from 'lucide-react';
import { toast } from 'sonner';
import VerificationUnavailableNotice from '@/components/rosters/VerificationUnavailableNotice';
import { VERIFICATION_UNAVAILABLE } from '@/lib/verificationStatus';

const DOC_TYPES = [
  { value: 'birth_certificate', label: 'Birth certificate' },
  { value: 'passport', label: 'Passport' },
  { value: 'school_report_card', label: 'School report card' },
];

function fmtDate(d?: string | null) {
  if (!d) return null;
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt.toLocaleDateString();
}

function StatusPill({ cred }: { cred: any }) {
  const status = cred?.status ?? 'none';
  if (status === 'cleared') {
    const exp = fmtDate(cred?.expires_at);
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200">
        <ShieldCheck className="mr-1 h-3 w-3" />
        Verified ✓{exp ? ` (expires ${exp})` : ''}
      </Badge>
    );
  }
  if (status === 'pending') {
    return (
      <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-200">
        <Clock className="mr-1 h-3 w-3" />
        Pending review
      </Badge>
    );
  }
  if (status === 'failed' || status === 'expired') {
    return (
      <Badge className="bg-red-100 text-red-800 hover:bg-red-100 border-red-200">
        <ShieldAlert className="mr-1 h-3 w-3" />
        {status === 'expired' ? 'Expired' : 'Rejected'}
      </Badge>
    );
  }
  return <Badge variant="secondary">Not submitted</Badge>;
}

interface Props {
  playerId: string;
  playerName?: string;
  orgId: string;
  requireGrade?: boolean;
  compact?: boolean;
}

export default function PlayerVerificationPanel({
  playerId, playerName, orgId, requireGrade = false, compact = false,
}: Props) {
  const queryClient = useQueryClient();
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [docType, setDocType] = useState('birth_certificate');
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data: creds = [] } = useQuery({
    queryKey: ['player-verification', playerId],
    enabled: !!playerId,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_player_verification', {
        p_player_id: playerId,
      });
      if (error) throw error;
      return Array.isArray(data) ? data : [];
    },
  });

  const byType = (t: string) => creds.find((c: any) => c.credential_type === t);

  const required = [
    { type: 'age_verification', label: 'Age verification' },
    ...(requireGrade ? [{ type: 'grade_verification', label: 'Grade verification' }] : []),
  ];

  const submit = async (credentialType: string) => {
    setSubmitting(true);
    try {
      let documentUrl: string | null = null;
      if (file) {
        const ext = file.name.split('.').pop() ?? 'bin';
        const path = `${orgId}/${playerId}/${credentialType}-${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('verification-docs')
          .upload(path, file, { upsert: false });
        if (upErr) throw upErr;
        documentUrl = path;
      }
      const { error } = await (supabase as any).rpc('submit_player_verification', {
        p_org_id: orgId,
        p_player_id: playerId,
        p_credential_type: credentialType,
        p_document_url: documentUrl,
        p_provider: DOC_TYPES.find((d) => d.value === docType)?.label ?? docType,
        p_notes: notes || null,
      });
      if (error) throw error;
      toast.success('Submitted for review.');
      setOpenFor(null);
      setFile(null);
      setNotes('');
      queryClient.invalidateQueries({ queryKey: ['player-verification', playerId] });
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={compact ? 'space-y-2' : 'rounded-md border p-3 space-y-3 bg-muted/30'}>
      {!compact && (
        <p className="text-sm font-medium">
          Verification{playerName ? ` · ${playerName}` : ''}
        </p>
      )}
      {VERIFICATION_UNAVAILABLE && <VerificationUnavailableNotice compact />}
      {required.map((req) => {
        const cred = byType(req.type);
        const cleared = cred?.status === 'cleared';
        const pending = cred?.status === 'pending';
        return (
          <div key={req.type} className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground w-32">{req.label}</span>
              <StatusPill cred={cred} />
              {!cleared && (
                <Button
                  size="sm"
                  variant={pending ? 'outline' : 'default'}
                  className="h-7 text-xs"
                  onClick={() => setOpenFor(openFor === req.type ? null : req.type)}
                >
                  <Upload className="mr-1 h-3 w-3" />
                  {pending ? 'Replace document' : 'Submit document'}
                </Button>
              )}
            </div>

            {openFor === req.type && (
              <div className="rounded-md border bg-background p-3 space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Document type</Label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOC_TYPES.map((d) => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Upload file (optional)</Label>
                  <Input
                    type="file"
                    accept="image/*,application/pdf"
                    className="h-9 text-sm"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Notes (optional)</Label>
                  <Textarea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Anything the tournament director should know"
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" disabled={submitting} onClick={() => submit(req.type)}>
                    {submitting ? 'Submitting…' : 'Submit for review'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setOpenFor(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <p className="text-[11px] text-muted-foreground">
        One-time submission — once verified, it&apos;s reused across this organization&apos;s events for the season.
      </p>
    </div>
  );
}
